<template>
  <div class="ml-12 mr-12 my-2 activity-container">
    <!-- Notification Dialog -->
    <NotificationDialog
      v-model:visible="showNotification"
      :type="notificationType"
      :title="notificationTitle"
      :message="notificationMessage"
      @close="showNotification = false"
    />

    <!-- Header Section -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card class="pa-4" style="border-radius: 15px; background: linear-gradient(135deg, #002060 0%, #001547 100%);">
          <h2 class="text-white mb-2">Executive Activity Log</h2>
          <p class="text-white text-opacity-80 mb-0">
            Comprehensive audit trail of all system activities and user actions
          </p>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters Row -->
    <v-row class="mb-4">
      <!-- Search input -->
      <v-col cols="12" sm="6" md="3">
        <v-text-field
          v-model="search"
          label="Search Activities"
          clearable
          class="elevation-0"
          @input="debouncedGetActivities"
          density="comfortable"
          bg-color="#e8e8e8"
          flat
          rounded
          append-inner-icon="mdi-magnify"
          variant="solo"
        />
      </v-col>

      <!-- Action Type filter -->
      <v-col cols="12" sm="6" md="3">
        <v-select
          v-model="actionFilter"
          :items="actionTypes"
          label="Filter by Action"
          clearable
          density="comfortable"
          bg-color="#e8e8e8"
          flat
          rounded
          variant="solo"
          @update:model-value="debouncedGetActivities"
        />
      </v-col>

      <!-- User filter -->
      <v-col cols="12" sm="6" md="2">
        <v-text-field
          v-model="userFilter"
          label="Filter by User"
          clearable
          density="comfortable"
          bg-color="#e8e8e8"
          flat
          rounded
          variant="solo"
          @input="debouncedGetActivities"
        />
      </v-col>

      <!-- Page filter -->
      <v-col cols="12" sm="6" md="2">
        <v-text-field
          v-model="pageFilter"
          label="Filter by Page"
          clearable
          density="comfortable"
          bg-color="#e8e8e8"
          flat
          rounded
          variant="solo"
          @input="debouncedGetActivities"
        />
      </v-col>

      <!-- Date filter -->
      <v-col cols="12" sm="6" md="2">
        <v-text-field
          v-model="dateFilter"
          label="Filter by Date"
          clearable
          density="comfortable"
          bg-color="#e8e8e8"
          flat
          rounded
          variant="solo"
          @input="debouncedGetActivities"
          hint="Format: MM/DD/YYYY"
        />
      </v-col>
    </v-row>

    <!-- Activity Table -->
    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="filteredActivities"
          :items-per-page="20"
          :loading="loading"
          class="custom-header elevation-3"
          style="border-radius: 17px;"
          loading-text="Loading activities... Please wait"
          :sort-by="sortBy"
          :sort-desc="sortDesc"
          :footer-props="{ 'items-per-page-options': [10,20,50,100] }"
          item-key="id"
        >
          <!-- Action Type Column -->
          <template v-slot:item.action="{ item }">
            <v-chip
              :color="getActionColor(item.action)"
              text-color="white"
              small
              class="ma-1"
            >
              <v-icon left size="small">{{ getActionIcon(item.action) }}</v-icon>
              {{ item.action }}
            </v-chip>
          </template>

          <!-- User Column -->
          <template v-slot:item.user="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="32" :color="getUserColor(item.user)" class="mr-2">
                <span class="avatar-text">{{ getUserInitials(item.user) }}</span>
              </v-avatar>
              <span>{{ item.user }}</span>
            </div>
          </template>

          <!-- Details Column -->
          <template v-slot:item.details="{ item }">
            <div class="details-content">
              <div class="details-main">{{ item.details }}</div>
              <div v-if="item.target" class="details-sub">
                Target: {{ item.target }}
              </div>
            </div>
          </template>

          <!-- Page Column -->
          <template v-slot:item.page="{ item }">
            <v-chip
              v-if="item.page"
              color="info"
              text-color="white"
              small
              class="ma-1"
            >
              {{ item.page }}
            </v-chip>
            <span v-else class="text-grey">N/A</span>
          </template>

          <!-- Timestamp Column -->
          <template v-slot:item.timestamp="{ item }">
            <div class="timestamp-content">
              <div class="timestamp-date">{{ formatDate(item.timestamp) }}</div>
              <div class="timestamp-time">{{ formatTime(item.timestamp) }}</div>
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { debounce } from 'lodash';
import { db } from '../firebaseConfig';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { mapGetters } from 'vuex';
import NotificationDialog from '../components/NotificationDialog.vue';
import { ActivityLogger } from '../utils/activityLogger';

export default {
  name: 'Activity',
  components: {
    NotificationDialog
  },
  data() {
    return {
      showNotification: false,
      notificationType: 'success',
      notificationTitle: '',
      notificationMessage: '',
      search: '',
      actionFilter: null,
      userFilter: '',
      pageFilter: '',
      dateFilter: '',
      loading: false,
      activities: [],
      headers: [
        { title: 'Action', key: 'action', sortable: true, width: '180px', align: 'center' },
        { title: 'User', key: 'user', sortable: true, width: '200px', align: 'center' },
        { title: 'Details', key: 'details', sortable: false, width: '350px', align: 'center' },
        { title: 'Page', key: 'page', sortable: true, width: '120px', align: 'center' },
        { title: 'Timestamp', key: 'timestamp', sortable: true, width: '180px', align: 'center' }
      ],
      actionTypes: [
        { title: 'All Actions', value: null },
        // Authentication
        { title: 'Login', value: 'Login' },
        { title: 'Logout', value: 'Logout' },
        { title: 'Login Failed', value: 'Login Failed' },
        { title: 'Password Reset', value: 'Password Reset Requested' },
        { title: 'Password Reset Completed', value: 'Password Reset Completed' },
        
        // User Management
        { title: 'User Created', value: 'User Created' },
        { title: 'User Updated', value: 'User Updated' },
        { title: 'User Deleted', value: 'User Deleted' },
        { title: 'User Viewed', value: 'User Viewed' },
        { title: 'User Status Changed', value: 'User Status Changed' },
        { title: 'User Role Changed', value: 'User Role Changed' },
        
        // Client Management
        { title: 'Client Created', value: 'Client Created' },
        { title: 'Client Updated', value: 'Client Updated' },
        { title: 'Client Deleted', value: 'Client Deleted' },
        { title: 'Client Viewed', value: 'Client Viewed' },
        { title: 'Client Searched', value: 'Client Searched' },
        { title: 'Client Exported', value: 'Client Exported' },
        
        // Member Management
        { title: 'Member Invited', value: 'Member Invited' },
        { title: 'Member Viewed', value: 'Member Viewed' },
        { title: 'Member Updated', value: 'Member Updated' },
        { title: 'Member Deleted', value: 'Member Deleted' },
        
        // Question & Answer
        { title: 'Question Created', value: 'Question Created' },
        { title: 'Question Updated', value: 'Question Updated' },
        { title: 'Question Deleted', value: 'Question Deleted' },
        { title: 'Question Viewed', value: 'Question Viewed' },
        { title: 'Question Responded', value: 'Question Responded' },
        { title: 'Question Status Changed', value: 'Question Status Changed' },
        { title: 'Question Assigned', value: 'Question Assigned' },
        
        // Profile Management
        { title: 'Profile Updated', value: 'Profile Updated' },
        { title: 'Profile Viewed', value: 'Profile Viewed' },
        { title: 'Password Changed', value: 'Password Changed' },
        
        // Navigation & Page Views
        { title: 'Page Viewed', value: 'Page Viewed' },
        { title: 'Dashboard Accessed', value: 'Dashboard Accessed' },
        { title: 'Activity Log Accessed', value: 'Activity Log Accessed' },
        { title: 'Reports Accessed', value: 'Reports Accessed' },
        
        // Search & Filter
        { title: 'Search Performed', value: 'Search Performed' },
        { title: 'Filter Applied', value: 'Filter Applied' },
        { title: 'Sort Changed', value: 'Sort Changed' },
        
        // Data Operations
        { title: 'Data Exported', value: 'Data Exported' },
        { title: 'Data Imported', value: 'Data Imported' },
        { title: 'Bulk Action Performed', value: 'Bulk Action Performed' },
        
        // System Actions
        { title: 'System Settings Changed', value: 'System Settings Changed' },
        { title: 'Backup Created', value: 'Backup Created' },
        { title: 'Logs Cleared', value: 'Logs Cleared' },
        
        // Email & Notifications
        { title: 'Email Sent', value: 'Email Sent' },
        { title: 'Email Failed', value: 'Email Failed' },
        { title: 'Notification Sent', value: 'Notification Sent' },
        
        // Security
        { title: 'Suspicious Activity', value: 'Suspicious Activity' },
        { title: 'Access Denied', value: 'Access Denied' },
        { title: 'Permission Violation', value: 'Permission Violation' }
      ],
      sortBy: [{ key: 'timestamp', order: 'desc' }],
      sortDesc: [true]
    };
  },

  computed: {
    ...mapGetters(['user', 'userRole']),
    
    filteredActivities() {
      return this.activities.filter(activity => {
        const matchesSearch = this.search
          ? [activity.action, activity.user, activity.details, activity.target, activity.page]
              .filter(Boolean)
              .some(field => field.toString().toLowerCase().includes(this.search.toLowerCase()))
          : true;

        const matchesAction = this.actionFilter
          ? activity.action === this.actionFilter
          : true;

        const matchesUser = this.userFilter
          ? activity.user.toLowerCase().includes(this.userFilter.toLowerCase())
          : true;

        const matchesPage = this.pageFilter
          ? activity.page && activity.page.toLowerCase().includes(this.pageFilter.toLowerCase())
          : true;

        const matchesDate = this.dateFilter
          ? this.formatDate(activity.timestamp) === this.dateFilter
          : true;

        return matchesSearch && matchesAction && matchesUser && matchesPage && matchesDate;
      });
    }
  },

  methods: {
    async getActivities() {
      this.loading = true;
      
      try {
        // Listen for real-time updates from Firestore
        if (this.unsubscribeActivities) this.unsubscribeActivities();
        
        const activitiesQuery = query(
          collection(db, 'activities'), 
          orderBy('timestamp', 'desc')
        );
        
        this.unsubscribeActivities = onSnapshot(activitiesQuery, (snapshot) => {
          this.activities = snapshot.docs.map(doc => ({ 
            id: doc.id, 
            ...doc.data(),
            timestamp: doc.data().timestamp?.toDate() || new Date()
          }));
          
          this.loading = false;
        }, (error) => {
          console.error('Error loading activities:', error);
          this.loading = false;
        });
      } catch (error) {
        console.error('Error setting up activities listener:', error);
        this.loading = false;
      }
    },

    getActionColor(action) {
      const colors = {
        // Authentication
        'Login': 'success',
        'Logout': 'info',
        'Login Failed': 'error',
        'Password Reset Requested': 'warning',
        'Password Reset Completed': 'success',
        
        // User Management
        'User Created': 'primary',
        'User Updated': 'warning',
        'User Deleted': 'error',
        'User Viewed': 'info',
        'User Status Changed': 'orange',
        'User Role Changed': 'purple',
        
        // Client Management
        'Client Created': 'primary',
        'Client Updated': 'warning',
        'Client Deleted': 'error',
        'Client Viewed': 'info',
        'Client Searched': 'blue',
        'Client Exported': 'teal',
        
        // Member Management
        'Member Invited': 'info',
        'Member Viewed': 'info',
        'Member Updated': 'warning',
        'Member Deleted': 'error',
        
        // Question & Answer
        'Question Created': 'primary',
        'Question Updated': 'warning',
        'Question Deleted': 'error',
        'Question Viewed': 'info',
        'Question Responded': 'success',
        'Question Status Changed': 'orange',
        'Question Assigned': 'purple',
        
        // Profile Management
        'Profile Updated': 'info',
        'Profile Viewed': 'info',
        'Password Changed': 'warning',
        
        // Navigation & Page Views
        'Page Viewed': 'blue',
        'Dashboard Accessed': 'success',
        'Activity Log Accessed': 'purple',
        'Reports Accessed': 'teal',
        
        // Search & Filter
        'Search Performed': 'blue',
        'Filter Applied': 'blue',
        'Sort Changed': 'blue',
        
        // Data Operations
        'Data Exported': 'teal',
        'Data Imported': 'teal',
        'Bulk Action Performed': 'orange',
        
        // System Actions
        'System Settings Changed': 'purple',
        'Backup Created': 'success',
        'Logs Cleared': 'warning',
        
        // Email & Notifications
        'Email Sent': 'success',
        'Email Failed': 'error',
        'Notification Sent': 'info',
        
        // Security
        'Suspicious Activity': 'error',
        'Access Denied': 'error',
        'Permission Violation': 'error'
      };
      return colors[action] || 'grey';
    },

    getActionIcon(action) {
      const icons = {
        // Authentication
        'Login': 'mdi-login',
        'Logout': 'mdi-logout',
        'Login Failed': 'mdi-login-variant',
        'Password Reset Requested': 'mdi-lock-reset',
        'Password Reset Completed': 'mdi-lock-check',
        
        // User Management
        'User Created': 'mdi-account-plus',
        'User Updated': 'mdi-account-edit',
        'User Deleted': 'mdi-account-remove',
        'User Viewed': 'mdi-account-eye',
        'User Status Changed': 'mdi-account-switch',
        'User Role Changed': 'mdi-account-cog',
        
        // Client Management
        'Client Created': 'mdi-account-group',
        'Client Updated': 'mdi-account-edit',
        'Client Deleted': 'mdi-account-remove',
        'Client Viewed': 'mdi-account-eye',
        'Client Searched': 'mdi-magnify',
        'Client Exported': 'mdi-download',
        
        // Member Management
        'Member Invited': 'mdi-email-send',
        'Member Viewed': 'mdi-account-eye',
        'Member Updated': 'mdi-account-edit',
        'Member Deleted': 'mdi-account-remove',
        
        // Question & Answer
        'Question Created': 'mdi-help-circle',
        'Question Updated': 'mdi-help-circle-outline',
        'Question Deleted': 'mdi-delete',
        'Question Viewed': 'mdi-eye',
        'Question Responded': 'mdi-reply',
        'Question Status Changed': 'mdi-flag',
        'Question Assigned': 'mdi-account-arrow-right',
        
        // Profile Management
        'Profile Updated': 'mdi-account-cog',
        'Profile Viewed': 'mdi-account-eye',
        'Password Changed': 'mdi-lock-reset',
        
        // Navigation & Page Views
        'Page Viewed': 'mdi-eye',
        'Dashboard Accessed': 'mdi-view-dashboard',
        'Activity Log Accessed': 'mdi-clipboard-list',
        'Reports Accessed': 'mdi-chart-bar',
        
        // Search & Filter
        'Search Performed': 'mdi-magnify',
        'Filter Applied': 'mdi-filter',
        'Sort Changed': 'mdi-sort',
        
        // Data Operations
        'Data Exported': 'mdi-download',
        'Data Imported': 'mdi-upload',
        'Bulk Action Performed': 'mdi-format-list-bulleted',
        
        // System Actions
        'System Settings Changed': 'mdi-cog',
        'Backup Created': 'mdi-backup-restore',
        'Logs Cleared': 'mdi-delete-sweep',
        
        // Email & Notifications
        'Email Sent': 'mdi-email-send',
        'Email Failed': 'mdi-email-alert',
        'Notification Sent': 'mdi-bell',
        
        // Security
        'Suspicious Activity': 'mdi-alert',
        'Access Denied': 'mdi-block-helper',
        'Permission Violation': 'mdi-shield-alert'
      };
      return icons[action] || 'mdi-information';
    },

    getUserColor(user) {
      // Generate consistent color based on username
      const hash = user.split('').reduce((a, b) => {
        a = ((a << 5) - a) + b.charCodeAt(0);
        return a & a;
      }, 0);
     const colors = ['#002060', '#BCA258', '#4caf50', '#ff9800', '#9c27b0', '#2196f3'];
      return colors[Math.abs(hash) % colors.length];
    },

    getUserInitials(user) {
      return user.split(' ').map(name => name[0]).join('').toUpperCase();
    },

    formatDate(timestamp) {
      if (!timestamp) return 'N/A';
      const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      });
    },

    formatTime(timestamp) {
      if (!timestamp) return 'N/A';
      const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    },

    debouncedGetActivities: debounce(function() {
      // Debounced search - filtering happens in computed property
    }, 300),

    logActivityLogAccess() {
      const currentUser = this.$store.getters.user;
      const currentUserId = currentUser?.uid;
      const currentUserName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Unknown';
      
      ActivityLogger.activityLogAccessed(currentUserName, currentUserId, 'Activity Log Page');
    }
  },

  created() {
    this.getActivities();
    this.logActivityLogAccess();
  },

  beforeUnmount() {
    if (this.unsubscribeActivities) {
      this.unsubscribeActivities();
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

* {
  font-family: 'Montserrat', sans-serif;
}

.activity-container {
  min-height: 100vh;
}

.custom-header {
  background-color: #e8e8e8;
}

.avatar-text {
  font-size: 0.8rem;
  font-weight: 600;
  color: white;
}

.details-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.details-main {
  font-weight: 500;
  color: #333;
}

.details-sub {
  font-size: 0.85rem;
  color: #666;
  font-style: italic;
}

.timestamp-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.timestamp-date {
  font-weight: 500;
  color: #333;
}

.timestamp-time {
  font-size: 0.85rem;
  color: #666;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .activity-container {
    margin: 8px !important;
  }
  
  .details-content {
    max-width: 200px;
  }
  
  .details-main {
    font-size: 0.9rem;
  }
}
</style> 